import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { trackPageView as trackMetaPageView } from "@/utils/metaPixel";
import { trackPageView as trackGA4PageView } from "@/utils/googleAnalytics";
import { initConsent } from "@/utils/consent";
import { applyForcedGeo } from "@/utils/geo";
import ConsentBanner from "@/components/ConsentBanner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import IntakePage from "@/pages/IntakePage";
import DigitalMarketingPage from "@/pages/DigitalMarketingPage";
import EVEngineersPage from "@/pages/EVEngineersPage";
import DataAnalystsPage from "@/pages/DataAnalystsPage";
import AccountManagersPage from "@/pages/AccountManagersPage";
import LPDigitalMarketingResultsPage from "@/pages/LPDigitalMarketingResultsPage";
import LPEVEngineersResultsPage from "@/pages/LPEVEngineersResultsPage";
import LPDataAnalystsResultsPage from "@/pages/LPDataAnalystsResultsPage";
import LPAccountManagersResultsPage from "@/pages/LPAccountManagersResultsPage";
import LPNewOfferResultsPage from "@/pages/LPNewOfferResultsPage";
import NewOfferPage from "@/pages/NewOfferPage";
import UpsellPage from "@/pages/UpsellPage";
import JobOnboardingPage from "@/pages/JobOnboardingPage";
import ChatbotPage from "@/pages/ChatbotPage";
import ResultsPage from "@/pages/ResultsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import TemplatePage from "@/pages/TemplatePage";
import SeasonPassPage from "@/pages/SeasonPassPage";
import DashboardPage from "@/pages/DashboardPage";
import RecruiterDashboardPage from "@/pages/RecruiterDashboardPage";
import JobFeedPage from "@/pages/JobFeedPage";
import CompanionPage from "@/pages/CompanionPage";
import SkillsPage from "@/pages/SkillsPage";
import AdminPage from "@/pages/AdminPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import FAQPage from "@/pages/FAQPage";
import NotFoundPage from "@/pages/NotFoundPage";
import "@/App.css";

// Routes that should render WITHOUT Navbar and Footer
const BARE_ROUTES = ["/lp/", "/upsell/session-pass", "/auth", "/forgot-password", "/recruiter"];
// The admin workspace is a self-contained operations console. Keep the site
// navbar for continuity, but do not append the public marketing footer after
// the viewport-sized admin shell (the fixed admin sidebar owns that edge).
const NO_SITE_FOOTER_ROUTES = ["/admin"];
const SITE_ORIGIN = "https://placementos.ai";

const CANONICAL_ROUTE_ALIASES = {
  "/lp/digitalmarketing": "/digitalmarketing",
  "/lp/newoffer": "/newoffer",
  "/lp/ev-engineers": "/ev-engineers",
  "/lp/data-analysts": "/data-analysts",
  "/lp/account-managers": "/account-managers",
};

const NOINDEX_ROUTE_PREFIXES = [
  "/admin",
  "/auth",
  "/chat",
  "/companion",
  "/dashboard",
  "/forgot-password",
  "/intake",
  "/jobs",
  "/lp/digitalmarketing/results",
  "/lp/ev-engineers/results",
  "/lp/data-analysts/results",
  "/lp/account-managers/results",
  "/lp/newoffer/results",
  "/payment/success",
  "/reset-password",
  "/results",
  "/recruiter",
  "/skills",
  "/templates",
  "/upsell/session-pass",
];

const INDEXABLE_ROUTES = new Set([
  "/",
  "/digitalmarketing",
  "/newoffer",
  "/ev-engineers",
  "/data-analysts",
  "/account-managers",
  "/lp/digitalmarketing",
  "/lp/newoffer",
  "/lp/ev-engineers",
  "/lp/data-analysts",
  "/lp/account-managers",
  "/find-jobs",
  "/pricing",
  "/get-started",
  "/season-pass",
  "/about",
  "/contact",
  "/privacy",
  "/terms",
  "/faq",
]);

const KNOWN_ROUTES = new Set([
  ...INDEXABLE_ROUTES,
  "/auth",
  "/forgot-password",
  "/reset-password",
  "/intake",
  "/chat",
  "/results",
  "/templates",
  "/jobs",
  "/companion",
  "/skills",
  "/admin",
  "/dashboard",
  "/recruiter",
  "/payment/success",
  "/upsell/session-pass",
  "/lp/digitalmarketing/results",
  "/lp/ev-engineers/results",
  "/lp/data-analysts/results",
  "/lp/account-managers/results",
  "/lp/newoffer/results",
]);

function normalizedPath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

function canonicalUrlForPath(pathname) {
  const path = normalizedPath(pathname);
  const canonicalPath = CANONICAL_ROUTE_ALIASES[path] || path;
  return canonicalPath === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${canonicalPath}`;
}

function robotsContentForPath(pathname) {
  const path = normalizedPath(pathname);
  const isNoindex = NOINDEX_ROUTE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
  return isNoindex || !INDEXABLE_ROUTES.has(path) ? "noindex, nofollow" : "index, follow";
}

function upsertHeadTag(selector, createTag, attributes) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = createTag();
    document.head.appendChild(tag);
  }
  Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value));
}

function applyRouteSeo(pathname) {
  upsertHeadTag(
    'link[rel="canonical"]',
    () => {
      const tag = document.createElement("link");
      tag.setAttribute("rel", "canonical");
      return tag;
    },
    { href: canonicalUrlForPath(pathname) }
  );
  upsertHeadTag(
    'meta[name="robots"]',
    () => {
      const tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      return tag;
    },
    { content: robotsContentForPath(pathname) }
  );
}

function isBareRoute(pathname) {
  const path = normalizedPath(pathname);
  return KNOWN_ROUTES.has(path) && BARE_ROUTES.some((r) => path === r || path.startsWith(r));
}

function isNoSiteFooterRoute(pathname) {
  const path = normalizedPath(pathname);
  return NO_SITE_FOOTER_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return user ? children : <Navigate to="/auth" />;
}

// SPA route changes keep the previous page's scroll offset — a visitor who
// scrolled the About page landed mid-page on Contact. Reset to top on forward
// navigations (link clicks); POP (back/forward) keeps the browser's own scroll
// restoration, and hash links keep their anchor behavior. Keyed on pathname
// only so query-param updates (e.g. /admin?tab=) never jump the page.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (hash || navType === "POP") return;
    window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function AppRoutes() {
  const location = useLocation();
  const isBare = isBareRoute(location.pathname);
  const hideSiteFooter = isNoSiteFooterRoute(location.pathname);

  // Boot: apply any ?geo= dev override synchronously (no network), then resolve
  // consent. Country is detected server-side (Cloudflare CF-IPCountry) via the
  // market-config call inside initConsent; only local dev falls back to a
  // client-side IP lookup. EEA/UK get gated + a banner; India/US/rest stay open.
  useEffect(() => { applyForcedGeo(); initConsent(); }, []);

  // Track page views on every route change (Meta Pixel + GA4)
  useEffect(() => {
    applyRouteSeo(location.pathname);

    // Track Meta Pixel
    trackMetaPageView();
    
    // Track Google Analytics 4
    const pagePath = location.pathname + location.search;
    const pageTitle = document.title;
    trackGA4PageView(pagePath, pageTitle);
  }, [location.pathname, location.search]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <ScrollToTop />
      {!isBare && <Navbar />}
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/digitalmarketing" element={<DigitalMarketingPage />} />
          <Route path="/newoffer" element={<NewOfferPage />} />
          <Route path="/ev-engineers" element={<EVEngineersPage />} />
          <Route path="/data-analysts" element={<DataAnalystsPage />} />
          <Route path="/account-managers" element={<AccountManagersPage />} />
          {/* LP variants render the same focused pages with the stripped LP header/footer shell. */}
          <Route path="/lp/digitalmarketing" element={<DigitalMarketingPage />} />
          <Route path="/lp/newoffer" element={<NewOfferPage />} />
          <Route path="/lp/ev-engineers" element={<EVEngineersPage />} />
          <Route path="/lp/data-analysts" element={<DataAnalystsPage />} />
          <Route path="/lp/account-managers" element={<AccountManagersPage />} />
          {/* LP Results Pages for A/B Testing */}
          <Route path="/lp/digitalmarketing/results" element={<LPDigitalMarketingResultsPage />} />
          <Route path="/lp/ev-engineers/results" element={<LPEVEngineersResultsPage />} />
          <Route path="/lp/data-analysts/results" element={<LPDataAnalystsResultsPage />} />
          <Route path="/lp/account-managers/results" element={<LPAccountManagersResultsPage />} />
          <Route path="/lp/newoffer/results" element={<LPNewOfferResultsPage />} />
          {/* Upsell Page — stripped header (Task 7) */}
          <Route path="/upsell/session-pass" element={<UpsellPage />} />
          {/* Job Onboarding Flow */}
          <Route path="/find-jobs" element={<JobOnboardingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
          <Route path="/get-started" element={<Navigate to="/auth?mode=signup" replace />} />
          <Route path="/intake" element={<IntakePage />} />
          <Route path="/chat" element={<ChatbotPage />} />
          <Route path="/results" element={<ResultsPage />} />
          <Route path="/templates" element={<ProtectedRoute><TemplatePage /></ProtectedRoute>} />
          <Route path="/season-pass" element={<SeasonPassPage />} />
          <Route path="/jobs" element={<JobFeedPage />} />
          <Route path="/companion" element={<ProtectedRoute><CompanionPage /></ProtectedRoute>} />
          <Route path="/skills" element={<ProtectedRoute><SkillsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/recruiter" element={<ProtectedRoute><RecruiterDashboardPage /></ProtectedRoute>} />
          {/* Payment success — NOT protected (LP users are guests) */}
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          {/* Public Pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
      {!isBare && !hideSiteFooter && <Footer />}
      <Toaster theme="dark" position="top-right" richColors />
      {/* EEA/UK-only cookie consent (self-hides for India/US/rest) */}
      <ConsentBanner />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
